import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Building2, User, Mail, Phone, Globe, CheckCircle } from 'lucide-react';

const DISCIPLINES = [
  "Principal Agent (PA)",
  "Project Manager (PM)",
  "Principal Quantity Surveyor (PQS)",
  "Architect (Arch)",
  "Structural Engineer",
  "Electrical Engineer",
  "Mechanical Engineer",
  "Wet Services Engineer",
  "Fire Engineer",
  "Lift Engineer",
  "Interior Designer",
  "Landscape Architect",
  "Civil Engineer",
  "Traffic Engineer",
  "Geotechnical Engineer",
  "Sustainability Consultant",
  "Health & Safety Agent",
  "Employer's Agent",
  "Client Representative",
  "Other"
];

const emptyForm = {
  company_name: '',
  company_registration: '',
  physical_address: '',
  postal_address: '',
  telephone: '',
  website: '',
  primary_contact: {
    name: '',
    surname: '',
    position: '',
    direct_telephone: '',
    mobile: '',
    email: ''
  },
  secondary_contact: null,
  discipline: '',
  discipline_other: '',
  is_default_distribution: false,
  is_mandatory_recipient: false
};

export default function ProfessionalTeamManager({ projectId, canEdit = true }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showSecondaryContact, setShowSecondaryContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterDiscipline, setFilterDiscipline] = useState('all');

  useEffect(() => {
    loadMembers();
  }, [projectId]);

  const loadMembers = async () => {
    try {
      const response = await api.professionalTeam.getAll(projectId);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load professional team');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = { ...form };
      
      // Handle secondary contact
      if (!showSecondaryContact) {
        data.secondary_contact = null;
      }

      if (editingMember) {
        await api.professionalTeam.update(projectId, editingMember.id, data);
        toast.success('Team member updated');
      } else {
        await api.professionalTeam.create(projectId, data);
        toast.success('Team member added');
      }

      setDialogOpen(false);
      setEditingMember(null);
      setForm(emptyForm);
      setShowSecondaryContact(false);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setForm({
      company_name: member.company_name || '',
      company_registration: member.company_registration || '',
      physical_address: member.physical_address || '',
      postal_address: member.postal_address || '',
      telephone: member.telephone || '',
      website: member.website || '',
      primary_contact: member.primary_contact || emptyForm.primary_contact,
      secondary_contact: member.secondary_contact || null,
      discipline: member.discipline || '',
      discipline_other: member.discipline_other || '',
      is_default_distribution: member.is_default_distribution || false,
      is_mandatory_recipient: member.is_mandatory_recipient || false
    });
    setShowSecondaryContact(!!member.secondary_contact);
    setDialogOpen(true);
  };

  const handleDelete = async (memberId) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      await api.professionalTeam.delete(projectId, memberId);
      toast.success('Team member deleted');
      loadMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const openAddDialog = () => {
    setEditingMember(null);
    setForm(emptyForm);
    setShowSecondaryContact(false);
    setDialogOpen(true);
  };

  const filteredMembers = filterDiscipline === 'all' 
    ? members 
    : members.filter(m => m.discipline === filterDiscipline);

  const groupedMembers = filteredMembers.reduce((acc, member) => {
    const discipline = member.discipline || 'Other';
    if (!acc[discipline]) acc[discipline] = [];
    acc[discipline].push(member);
    return acc;
  }, {});

  if (loading) {
    return <div className="p-4 text-slate-500">Loading professional team...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-barlow font-bold uppercase tracking-tight">Professional Team Directory</h3>
          <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
            <SelectTrigger className="w-[200px] rounded-sm">
              <SelectValue placeholder="Filter by discipline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Disciplines</SelectItem>
              {DISCIPLINES.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button 
            onClick={openAddDialog}
            data-testid="add-consultant-button"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-2"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Consultant
          </Button>
        )}
      </div>

      <div className="text-sm text-slate-500 mb-4">
        {members.length} consultant(s) registered
      </div>

      {Object.keys(groupedMembers).length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-8 text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No professional team members added yet.</p>
          {canEdit && (
            <Button onClick={openAddDialog} variant="outline" className="mt-4">
              Add First Consultant
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMembers).map(([discipline, disciplineMembers]) => (
            <div key={discipline} className="space-y-2">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
                {discipline} ({disciplineMembers.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {disciplineMembers.map((member) => (
                  <div 
                    key={member.id} 
                    className="bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h5 className="font-bold text-slate-900">{member.company_name}</h5>
                        <p className="text-sm text-slate-600">
                          {member.primary_contact?.name} {member.primary_contact?.surname}
                          {member.primary_contact?.position && ` - ${member.primary_contact.position}`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {member.is_default_distribution && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            Default
                          </Badge>
                        )}
                        {member.is_mandatory_recipient && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            Mandatory
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1 text-sm">
                      {member.primary_contact?.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{member.primary_contact.email}</span>
                        </div>
                      )}
                      {member.primary_contact?.mobile && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-3 w-3" />
                          <span>{member.primary_contact.mobile}</span>
                        </div>
                      )}
                      {member.telephone && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="h-3 w-3" />
                          <span>{member.telephone} (Office)</span>
                        </div>
                      )}
                    </div>

                    {canEdit && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button
                          onClick={() => handleEdit(member)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-slate-900"
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDelete(member.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">
              {editingMember ? 'Edit Consultant' : 'Add Consultant'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Discipline Selection */}
            <div className="bg-slate-50 p-4 rounded-sm space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Discipline</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Consultant Discipline *
                  </Label>
                  <Select 
                    value={form.discipline} 
                    onValueChange={(v) => setForm({ ...form, discipline: v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCIPLINES.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.discipline === 'Other' && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                      Specify Discipline
                    </Label>
                    <Input
                      value={form.discipline_other}
                      onChange={(e) => setForm({ ...form, discipline_other: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Company Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Company Name *
                  </Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Registration Number
                  </Label>
                  <Input
                    value={form.company_registration}
                    onChange={(e) => setForm({ ...form, company_registration: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Physical Address
                  </Label>
                  <Textarea
                    value={form.physical_address}
                    onChange={(e) => setForm({ ...form, physical_address: e.target.value })}
                    rows={2}
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Postal Address
                  </Label>
                  <Textarea
                    value={form.postal_address}
                    onChange={(e) => setForm({ ...form, postal_address: e.target.value })}
                    rows={2}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Telephone (Main)
                  </Label>
                  <Input
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Website
                  </Label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Primary Contact */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <User className="h-4 w-4" />
                Primary Contact Person *
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Name *</Label>
                  <Input
                    value={form.primary_contact.name}
                    onChange={(e) => setForm({ 
                      ...form, 
                      primary_contact: { ...form.primary_contact, name: e.target.value }
                    })}
                    required
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Surname *</Label>
                  <Input
                    value={form.primary_contact.surname}
                    onChange={(e) => setForm({ 
                      ...form, 
                      primary_contact: { ...form.primary_contact, surname: e.target.value }
                    })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Position</Label>
                  <Input
                    value={form.primary_contact.position}
                    onChange={(e) => setForm({ 
                      ...form, 
                      primary_contact: { ...form.primary_contact, position: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Direct Tel</Label>
                  <Input
                    value={form.primary_contact.direct_telephone}
                    onChange={(e) => setForm({ 
                      ...form, 
                      primary_contact: { ...form.primary_contact, direct_telephone: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Mobile</Label>
                  <Input
                    value={form.primary_contact.mobile}
                    onChange={(e) => setForm({ 
                      ...form, 
                      primary_contact: { ...form.primary_contact, mobile: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Email *</Label>
                <Input
                  type="email"
                  value={form.primary_contact.email}
                  onChange={(e) => setForm({ 
                    ...form, 
                    primary_contact: { ...form.primary_contact, email: e.target.value }
                  })}
                  required
                  className="rounded-sm"
                />
              </div>
            </div>

            {/* Secondary Contact Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="secondary-contact"
                checked={showSecondaryContact}
                onCheckedChange={(checked) => {
                  setShowSecondaryContact(checked);
                  if (!checked) {
                    setForm({ ...form, secondary_contact: null });
                  } else {
                    setForm({ 
                      ...form, 
                      secondary_contact: { name: '', surname: '', position: '', direct_telephone: '', email: '' }
                    });
                  }
                }}
              />
              <Label htmlFor="secondary-contact" className="text-sm cursor-pointer">
                Add Secondary Contact Person
              </Label>
            </div>

            {/* Secondary Contact */}
            {showSecondaryContact && (
              <div className="space-y-4 bg-slate-50 p-4 rounded-sm">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Secondary Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Name</Label>
                    <Input
                      value={form.secondary_contact?.name || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        secondary_contact: { ...form.secondary_contact, name: e.target.value }
                      })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Surname</Label>
                    <Input
                      value={form.secondary_contact?.surname || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        secondary_contact: { ...form.secondary_contact, surname: e.target.value }
                      })}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Position</Label>
                    <Input
                      value={form.secondary_contact?.position || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        secondary_contact: { ...form.secondary_contact, position: e.target.value }
                      })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Direct Tel</Label>
                    <Input
                      value={form.secondary_contact?.direct_telephone || ''}
                      onChange={(e) => setForm({ 
                        ...form, 
                        secondary_contact: { ...form.secondary_contact, direct_telephone: e.target.value }
                      })}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={form.secondary_contact?.email || ''}
                    onChange={(e) => setForm({ 
                      ...form, 
                      secondary_contact: { ...form.secondary_contact, email: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            )}

            {/* Distribution Settings */}
            <div className="space-y-4 bg-blue-50 p-4 rounded-sm">
              <h4 className="text-sm font-bold uppercase tracking-wider text-blue-700">Distribution Settings</h4>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="default-distribution"
                    checked={form.is_default_distribution}
                    onCheckedChange={(checked) => setForm({ ...form, is_default_distribution: checked })}
                  />
                  <Label htmlFor="default-distribution" className="text-sm cursor-pointer">
                    Default Distribution
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="mandatory-recipient"
                    checked={form.is_mandatory_recipient}
                    onCheckedChange={(checked) => setForm({ ...form, is_mandatory_recipient: checked })}
                  />
                  <Label htmlFor="mandatory-recipient" className="text-sm cursor-pointer">
                    Mandatory Recipient
                  </Label>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={saving || !form.discipline || !form.company_name}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
            >
              {saving ? 'Saving...' : (editingMember ? 'Update Consultant' : 'Add Consultant')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
